/**
 * Calendar-date (yyyy-mm-dd) helpers for the forward-availability feature
 * (migrations 669/670). All parsing is LOCAL-timezone on purpose: a DATE
 * column has no timezone, and `new Date('yyyy-mm-dd')` would parse as UTC
 * midnight — shifting the displayed day backwards west of Greenwich.
 *
 * SIL-322: parseISODate/formatDayMonthYear moved to @siliconx/shared/ui/format
 * (consolidated with the identical formatDateOnly duplicate in ops
 * listings.tsx) — re-exported here so existing call sites are unaffected.
 */
import { parseISODate, formatDayMonthYear } from '@siliconx/shared/ui/format';

export { parseISODate, formatDayMonthYear };

/** Today as a local yyyy-mm-dd string. */
export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** yyyy-mm-dd for `days` days after today (local). */
export function addDaysISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** yyyy-mm-dd for `months` months after today (local). */
export function addMonthsISO(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** True only when `iso` is a calendar date strictly AFTER today (local). */
export function isFutureDate(iso: string | null | undefined): boolean {
  return iso != null && iso.slice(0, 10) > todayISO();
}

/** 'Jul 2026' */
export function formatMonthYear(iso: string): string {
  return parseISODate(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * A buyer's delivery window as compact copy:
 *   from + by   → 'Jul–Dec 2026' (or 'Jul 2026–Feb 2027' across years)
 *   by only     → 'By Dec 2026'
 *   from only   → 'From Jul 2026'
 *   neither     → 'Any time'
 */
export function formatDeliveryWindow(
  from: string | null | undefined,
  by: string | null | undefined,
): string {
  if (from && by) {
    const f = parseISODate(from);
    const b = parseISODate(by);
    if (f.getFullYear() === b.getFullYear()) {
      const fm = f.toLocaleDateString('en-US', { month: 'short' });
      return `${fm}–${formatMonthYear(by)}`;
    }
    return `${formatMonthYear(from)}–${formatMonthYear(by)}`;
  }
  if (by) return `By ${formatMonthYear(by)}`;
  if (from) return `From ${formatMonthYear(from)}`;
  return 'Any time';
}

/**
 * Convert a picked yyyy-mm-dd expiration date to the end of that day (local)
 * as an ISO timestamp — what the `expiresAt` API inputs expect.
 */
export function endOfDayISO(iso: string): string {
  const d = parseISODate(iso);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).toISOString();
}
